<template>
  <div class="mh" data-test="pfe-medhx-body">
    <div class="mh-top">
      <send-switch-card
        test-id="pfe-mh"
        title="Send medical history update reminders"
        :state="switchView"
        :busy="saving"
        :error="switchError"
        :confirm-open="confirmOpen"
        confirm-title="Turn on medical history reminders?"
        confirm-body="Patients who are due get the reminder with their next appointment confirmation."
        @request-on="onRequestOn"
        @turn-off="onTurnOff"
        @confirm="onConfirm"
        @cancel="confirmOpen = false"
      />
      <ul v-if="serverProblems.length" class="mh-problems" role="alert" data-test="pfe-mh-server-problems">
        <li v-for="(p, i) in serverProblems" :key="i">{{ p }}</li>
      </ul>
    </div>

    <div class="mh-cols">
      <div class="mh-left">
        <section class="mh-card" aria-labelledby="mh-often-title">
          <span id="mh-often-title" class="mh-card-title">How often</span>
          <div class="mh-line">
            <span>Patients should update their medical history every</span>
            <input
              class="mh-num"
              :class="{ 'mh-field--bad': !!intervalProblem }"
              type="text"
              inputmode="numeric"
              maxlength="2"
              autocomplete="off"
              aria-label="How many"
              :aria-invalid="intervalProblem ? 'true' : 'false'"
              :value="intervalText"
              data-test="pfe-mh-interval"
              @input="onInterval"
            />
            <select class="mh-select" aria-label="Unit" :value="work.intervalUnit" data-test="pfe-mh-unit" @change="onUnit($event.target.value)">
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
          </div>
          <div v-if="intervalProblem" class="mh-error" role="alert" data-test="pfe-mh-interval-error">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ intervalProblem.message }}
          </div>
          <span class="mh-help">e.g. 12 months, or 2 years.</span>
        </section>

        <section class="mh-card" aria-labelledby="mh-counts-title">
          <span id="mh-counts-title" class="mh-card-title">What counts as an update</span>
          <span class="mh-help mh-help--lead">The clock restarts whenever a patient submits:</span>
          <check-picker
            :value="triggerIds"
            :options="triggerOptions"
            :fallback-names="triggerFallbacks"
            display="chips"
            placeholder="Pick forms"
            search-placeholder="Search forms"
            aria-label="Forms that restart the clock"
            empty-text="No forms yet. Create one in Forms first."
            test-id="pfe-mh-counts"
            @input="onTriggerForms"
          />
          <div v-if="noTriggerProblem" class="mh-error" role="alert" data-test="pfe-mh-counts-error">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ noTriggerProblem.message }}
          </div>
          <span class="mh-help">Patients who have never submitted one of these aren’t reminded.</span>
        </section>

        <section class="mh-card" aria-labelledby="mh-when-title">
          <span id="mh-when-title" class="mh-card-title">When we remind</span>
          <p class="mh-text" data-test="pfe-mh-when">The reminder goes out with the patient’s next appointment confirmation — or, if confirmations are off, with their next reminder — so it lands when a visit is coming up.</p>
          <div class="mh-note-line">
            <i class="fa fa-info-circle" aria-hidden="true"></i>
            <span>Appointment Reminders must be on for this to send.</span>
            <router-link :to="{ name: 'AptReminder' }" target="_blank" rel="noopener" class="mh-link" data-test="pfe-mh-reminders-link">Open Appointment Reminders →</router-link>
          </div>
        </section>

        <div class="mh-msg-head">
          <span class="mh-card-title">Messages</span>
          <span class="mh-sub">Patients get one link to the form that restarts the clock.</span>
        </div>

        <div class="mh-cards" data-test="pfe-mh-cards">
          <forms-message-card
            v-for="ch in channels"
            :key="ch"
            :test-id="'pfe-mh-' + ch"
            :slot-key="'medhx-' + ch"
            :channel="ch"
            :value="cardValue(ch)"
            :mode="slots[ch].mode === 'page' ? 'template' : 'box'"
            :default-text="defaultText(ch)"
            :default-subject="defaultSubject"
            :forms="forms"
            :packets="packets"
            :catalog-failed="catalogFailed"
            :site="site"
            @input="onCard(ch, $event)"
          >
            <!-- A saved page (an office that set this up before plain messages): the same pages the old popup listed, then "Switch to a plain message" -->
            <template #template>
              <div class="mh-saved" :data-test="'pfe-mh-' + ch + '-saved-page'">
                <label class="mh-saved-label" :for="'mh-template-' + ch">Template</label>
                <select
                  :id="'mh-template-' + ch"
                  class="mh-select mh-select--wide"
                  :class="{ 'mh-field--bad': !!templateProblem(ch) }"
                  :value="savedPageId(ch)"
                  aria-label="Template"
                  :data-test="'pfe-mh-' + ch + '-template'"
                  @change="onSavedPage(ch, $event.target.value)"
                >
                  <option value="">Select a template…</option>
                  <option v-for="p in savedPageOptions(ch)" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
                <div v-if="pagesLoading" class="mh-help" :data-test="'pfe-mh-' + ch + '-template-loading'"><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading your templates…</div>
                <div v-if="templateProblem(ch)" class="mh-error" role="alert" :data-test="'pfe-mh-' + ch + '-template-error'">
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ templateProblem(ch).message }}
                </div>
                <div class="mh-saved-note">
                  <i class="fa fa-file-text-o" aria-hidden="true"></i>
                  <span>Sends this saved page as it is.</span>
                  <span aria-hidden="true">&middot;</span>
                  <button type="button" class="mh-link-btn" :data-test="'pfe-mh-' + ch + '-to-plain'" @click="toPlain(ch)">Switch to a plain message</button>
                </div>
              </div>
            </template>

            <template #footer>
              <div v-if="restartWarning(ch)" class="mh-warn" role="status" :data-test="'pfe-mh-' + ch + '-restart-warning'">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>Submitting this form won’t restart the clock — add it to What counts as an update.</span>
              </div>
              <div v-if="notes[ch]" class="mh-help" :data-test="'pfe-mh-' + ch + '-note'">{{ notes[ch] }}</div>
              <div v-if="slots[ch].page" class="mh-foot-links">
                <button type="button" class="mh-link-btn" :data-test="'pfe-mh-' + ch + '-to-template'" @click="toTemplate(ch)">Use a template instead</button>
              </div>
            </template>
          </forms-message-card>
        </div>
      </div>

      <forms-preview-pane
        test-id="pfe-mh-preview"
        :channel="previewChannel"
        :message="previewMessage"
        :site="site"
        :forms="forms"
        :packets="packets"
        :sample="sample"
        apt-word="appointment"
        @update:channel="previewChannel = $event"
      />
    </div>
  </div>
</template>

<script>
import SendSwitchCard from './SendSwitchCard.vue';
import FormsMessageCard from './FormsMessageCard.vue';
import FormsPreviewPane from './FormsPreviewPane.vue';
import CheckPicker from './CheckPicker.vue';
import tabMixin from './formsSettingsTab';
import { saveFormsSettingsSection } from '@/api/formsSettings';
import { wikiContentToPlainText, wikiContentToEmailText } from '@/utils/wikiPlainText';
import {
  MEDHX_DEFAULT_SMS_TEXT, MEDHX_DEFAULT_EMAIL_TEXT, MEDHX_DEFAULT_EMAIL_SUBJECT, buildMedHxInlineSlot,
  validateMedHxSettings, medHxBlockReasons, linkRestartsClock,
} from '@/utils/formsSettings/medHx';
import {
  medHxLoad, medHxBlob, medHxSavePayload, medHxSignature, medHxSwitchReasons, sendSwitchState, inlineSlotFrom, cardFromInlineSlot, formTypeTag,
  medHxWikiPages, packetFormIdsFor,
} from '@/utils/formsSettings/editorModel';

const clone = (v) => JSON.parse(JSON.stringify(v));
const oneLine = (v) => String(v === null || v === undefined ? '' : v).replace(/\s+/g, ' ').trim();
const UNREADABLE_NOTE = 'We couldn’t read that template, so this started as the default message.';
// The page linked a form / packet that is not in the site's lists (wikiPlainText: `formLink` present but null) — without this the box
// looked like a page that never had a link (found on dev 2026-10-23).
const DELETED_LINK_NOTE = 'The template’s link pointed to a deleted form — choose one.';

/** The load result with a `touched` flag per slot (true once the plain message holds the office's own words, so going back and forth keeps them). */
function withTouched(slots) {
  return { sms: Object.assign({ touched: false }, slots.sms), email: Object.assign({ touched: false }, slots.email) };
}

/**
 * Forms Settings → Medical History Update (plan docs/plans/2026-10-20-forms-settings-plan.md §0 D7 D13, §1f item 5, §2.1; board frames 2a–2d).
 *
 *   the switch card  "Send medical history update reminders" — the blob's `activated` (a JSON boolean). Reasons are WP0 medHxBlockReasons over the
 *                    working copy. Confirm sends the content (when edited) + activated:true in ONE request — the only place `activated` is sent;
 *                    turn-off sends { activated:false }.
 *   How often        "every [n] [months|years]" — a whole number 1–99, typed as text, kept as a NUMBER in the blob
 *   What counts      the forms that restart the clock (`triggerForms`), with a check-mark menu
 *   Messages         a Text card and an Email card. Each slot is either a PLAIN message (`inline`, with the Form link chip) or — for an office that set
 *                    this up with saved pages — a Template dropdown. "Switch to a plain message" fills the box from the page (links carried over as the
 *                    one Form link); "Use a template instead" goes back to the remembered page; the plain text stays in memory until Save.
 *
 * ⛔ THE BLOB KEEPS ITS TYPES (the live sender reads it): `activated` boolean, `channels` booleans, `intervalNumber` a number, the slot shapes as stored.
 *    A wiki slot is sent back exactly as loaded; its sender path is untouched. (Deploy order: forms-service WP1 before any frontend that can save an inline slot.)
 *
 * THE HOST CONTRACT (src/utils/formsSettings/host.js, mixin ./formsSettingsTab): `dirty` only after the office edited THIS tab (opening on the WP0 defaults
 * for a never-set-up office does not); `errorCount` = WP0 validateMedHxSettings over the working copy with the STORED `activated`; save() sends section
 * 'medHx' with every content key and never `activated`; reset() restores what the server has (and the in-memory plain / template choices).
 */
export default {
  name: 'PfeMedHxTab',
  tabKey: 'medHx',
  components: { SendSwitchCard, FormsMessageCard, FormsPreviewPane, CheckPicker },
  mixins: [tabMixin],
  data() {
    const model = medHxLoad(this.context && this.context.settings);
    const slots = withTouched(model.slots);
    return {
      channels: ['sms', 'email'],
      defaultSubject: MEDHX_DEFAULT_EMAIL_SUBJECT,

      work: clone(model.work),
      slots: clone(slots),
      baseline: { work: clone(model.work), slots: clone(slots) },
      baselineSig: medHxSignature(model),
      activated: model.activated,        // the switch as the server holds it
      intervalText: model.work.intervalNumber === '' ? '' : String(model.work.intervalNumber),

      confirmOpen: false,
      previewChannel: 'sms',
      notes: { sms: '', email: '' },
      savedPages: [],
      pagesLoaded: false,
      pagesLoading: false,
    };
  },
  computed: {
    model() { return { work: this.work, slots: this.slots }; },

    // ── the host contract ──
    dirty() { return medHxSignature(this.model) !== this.baselineSig; },
    problems() { return validateMedHxSettings(medHxBlob(this.model, this.activated)); },
    errorCount() { return this.problems.length; },

    // ── the switch card ──
    liveReasons() { return medHxBlockReasons(medHxBlob(this.model, true), { smsReady: this.smsReady, emailReady: this.emailReady }); },
    reasons() { return medHxSwitchReasons(this.context && this.context.blockReasons, this.liveReasons); },
    switchView() {
      return sendSwitchState({
        enabled: this.activated,
        reasons: this.reasons,
        offMessage: 'Off — patients aren’t reminded to update their medical history.',
        onMessage: 'On. Due patients get it with their next appointment confirmation.',
        onMessageNoDate: 'On. Due patients get it with their next appointment confirmation.',
      });
    },

    // ── the cards ──
    intervalProblem() { return this.problems.find((p) => p.path === 'intervalNumber' || p.path === 'intervalUnit') || null; },
    noTriggerProblem() { return this.problems.find((p) => p.code === 'NO_TRIGGER_FORMS') || null; },
    triggerOptions() {
      return this.forms
        .filter((f) => f && f.formIDForWeb)
        .map((f) => ({ value: String(f.formIDForWeb), name: f.name || '', tag: formTypeTag(f.formType) }));
    },
    triggerIds() { return this.work.triggerForms.map((f) => f.FormIDForWeb); },
    triggerFallbacks() {
      const names = {};
      this.work.triggerForms.forEach((f) => { names[f.FormIDForWeb] = f.Name || f.FormIDForWeb; });
      return names;
    },

    // ── preview ──
    previewMessage() {
      const ch = this.previewChannel;
      const state = this.slots[ch];
      const on = this.work.channels[ch];
      if (state.mode !== 'page') return cardFromInlineSlot(state.inline, ch, on);
      const page = this.savedPageFor(state.page);
      const converted = page ? this.convertPage(page, ch) : null;
      const out = { on, body: converted && converted.ok ? converted.text : '', formLink: converted && converted.formLink ? converted.formLink : null };
      if (ch === 'email') out.subject = page ? (oneLine(page.EmailSubject) || oneLine(page.Name)) : '';
      return out;
    },
  },
  watch: {
    context(next, prev) {
      if (next === prev || this.dirty || this.saving) return;
      this.adopt(next && next.settings);
    },
  },
  mounted() {
    // an office that still sends a saved page needs the pages (the dropdown, the preview, "Switch to a plain message")
    if (this.channels.some((ch) => this.slots[ch].mode === 'page')) this.ensurePages();
  },
  methods: {
    defaultText(channel) { return channel === 'email' ? MEDHX_DEFAULT_EMAIL_TEXT : MEDHX_DEFAULT_SMS_TEXT; },

    // ── "How often" / "What counts" ──
    onInterval(e) {
      const text = String(e.target.value).replace(/\D/g, '').slice(0, 2);
      e.target.value = text; // numbers only: a typed letter never stays in the box
      this.clearFailure();
      this.intervalText = text;
      this.work.intervalNumber = text === '' ? '' : Number(text);
    },
    onUnit(unit) {
      this.clearFailure();
      this.work.intervalUnit = unit;
    },
    onTriggerForms(ids) {
      this.clearFailure();
      this.work.triggerForms = ids.map((id) => {
        const option = this.triggerOptions.find((o) => o.value === id);
        return { FormIDForWeb: id, Name: (option && option.name) || this.triggerFallbacks[id] || '' };
      });
    },

    // ── the two message cards ──
    cardValue(channel) {
      const state = this.slots[channel];
      if (state.mode === 'page') return { on: this.work.channels[channel], body: '', formLink: null, subject: '' };
      return cardFromInlineSlot(state.inline, channel, this.work.channels[channel]);
    },
    onCard(channel, card) {
      this.clearFailure();
      this.$set(this.work.channels, channel, card.on === true);
      const state = this.slots[channel];
      if (state.mode === 'page') return; // a saved page has no text of its own to edit: only the switch
      state.inline = inlineSlotFrom(channel, card);
      state.touched = true;
      this.notes[channel] = '';
    },
    restartWarning(channel) {
      const state = this.slots[channel];
      if (state.mode === 'page' || !this.work.channels[channel]) return false;
      const link = state.inline.inline.formLink;
      if (!link) return false;
      return !linkRestartsClock(link, this.work.triggerForms, packetFormIdsFor(link, this.packets));
    },
    templateProblem(channel) {
      return this.work.channels[channel] ? this.problems.find((p) => p.path === `${channel}Template` && p.code === 'TEMPLATE_MISSING') || null : null;
    },

    // ── saved pages ──
    async ensurePages() {
      if (this.pagesLoaded) return;
      if (this.pagesPromise) { await this.pagesPromise; return; }
      this.pagesLoading = true;
      this.pagesPromise = (async () => {
        try {
          const data = await this.$store.dispatch('wiki/getWikiPages', { siteID: this.siteId, silent: true });
          this.savedPages = (data && data[0]) || [];
        } catch (e) {
          this.savedPages = [];
        } finally {
          this.pagesLoaded = true;
          this.pagesLoading = false;
        }
      })();
      await this.pagesPromise;
    },
    savedPageFor(slot) {
      return slot && slot.WikiPageID ? this.savedPages.find((p) => p.WikiPageID === slot.WikiPageID) || null : null;
    },
    savedPageId(channel) {
      const slot = this.slots[channel].page;
      return slot && slot.WikiPageID ? slot.WikiPageID : '';
    },
    /** What the old popup listed, plus the page the slot already names (a page since retagged or deleted still shows rather than blanking the dropdown). */
    savedPageOptions(channel) {
      const list = medHxWikiPages(this.savedPages, channel).map((p) => ({ id: p.WikiPageID, name: p.Name }));
      const slot = this.slots[channel].page;
      if (slot && slot.WikiPageID && !list.some((p) => p.id === slot.WikiPageID)) list.push({ id: slot.WikiPageID, name: slot.Name || 'Saved template' });
      return list;
    },
    onSavedPage(channel, id) {
      this.clearFailure();
      const option = this.savedPageOptions(channel).find((p) => p.id === id);
      this.slots[channel].page = id && option ? { WikiPageID: id, Name: option.name } : null;
    },
    convertPage(page, channel) {
      const opts = { allowFormLink: true, forms: this.forms, packets: this.packets };
      return channel === 'email' ? wikiContentToEmailText(page.Content, opts) : wikiContentToPlainText(page.Content, opts);
    },
    /** "Switch to a plain message": the box starts as the page's words (or what the office already typed here this session); the page's form link is the pick. */
    async toPlain(channel) {
      this.clearFailure();
      const state = this.slots[channel];
      if (!state.touched && state.page) { // no page picked yet (or the plain text is already the office's own): nothing to read
        await this.ensurePages();
        const page = this.savedPageFor(state.page);
        const converted = page ? this.convertPage(page, channel) : null;
        if (converted && converted.ok) {
          state.inline = buildMedHxInlineSlot(channel, {
            text: converted.text,
            subject: channel === 'email' ? (oneLine(page.EmailSubject) || oneLine(page.Name) || MEDHX_DEFAULT_EMAIL_SUBJECT) : undefined,
            formLink: converted.formLink || null,
          });
          state.touched = true;
          this.notes[channel] = converted.formLink === null ? DELETED_LINK_NOTE : '';
        } else {
          state.inline = buildMedHxInlineSlot(channel);
          this.notes[channel] = UNREADABLE_NOTE;
        }
      }
      state.mode = 'inline';
    },
    /** "Use a template instead": back to the remembered page (the plain text stays in memory until Save). */
    async toTemplate(channel) {
      this.clearFailure();
      this.notes[channel] = '';
      this.slots[channel].mode = 'page';
      await this.ensurePages();
    },

    // ── the host contract ──
    validate() { return this.errorCount; },
    /** Content only — ⛔ never `activated`. Never throws. */
    async save() {
      if (this.saveBusy) return { ok: false, message: 'Still saving — one moment.' };
      this.saveBusy = true;
      this.clearFailure();
      try {
        const res = await saveFormsSettingsSection({ auth: this.auth(), siteID: this.siteId, section: 'medHx', value: medHxSavePayload(this.model) });
        if (res && res.settings) this.adopt(res.settings);
        else this.markSaved();
        return { ok: true };
      } catch (e) {
        this.showFailure(e, 'Couldn’t save — please try again.');
        return { ok: false, message: this.switchError };
      } finally {
        this.saveBusy = false;
      }
    },
    reset() {
      this.work = clone(this.baseline.work);
      this.slots = clone(this.baseline.slots);
      this.intervalText = this.work.intervalNumber === '' ? '' : String(this.work.intervalNumber);
      this.notes = { sms: '', email: '' };
      this.clearFailure();
      this.confirmOpen = false;
    },
    /** The working copy is what the server has now (a save whose answer carried no settings). */
    markSaved() {
      this.baseline = { work: clone(this.work), slots: clone(this.slots) };
      this.baselineSig = medHxSignature(this.model);
    },
    adopt(settings) {
      const model = medHxLoad(settings);
      const slots = withTouched(model.slots);
      this.work = clone(model.work);
      this.slots = clone(slots);
      this.baseline = { work: clone(model.work), slots: clone(slots) };
      this.baselineSig = medHxSignature(model);
      this.activated = model.activated;
      this.intervalText = model.work.intervalNumber === '' ? '' : String(model.work.intervalNumber);
      this.notes = { sms: '', email: '' };
    },

    // ── the switch (the ONLY place `activated` is sent) ──
    onRequestOn() {
      this.clearFailure();
      if (this.reasons.length > 0) return;
      this.confirmOpen = true;
    },
    async onConfirm() {
      this.confirmOpen = false;
      if (this.switchBusy || this.reasons.length > 0) return;
      this.clearFailure();
      this.switchBusy = true;
      try {
        // unsaved edits go in the SAME request (the server's gate reads what it is about to store); an untouched tab sends just the switch
        const value = this.dirty ? Object.assign(medHxSavePayload(this.model), { activated: true }) : { activated: true };
        const res = await saveFormsSettingsSection({ auth: this.auth(), siteID: this.siteId, section: 'medHx', value });
        if (res && res.settings) this.adopt(res.settings);
        else { this.markSaved(); this.activated = true; }
        this.$emit('toast', 'Medical history reminders are on');
      } catch (e) {
        this.showFailure(e, 'Couldn’t turn this on. Please try again.');
      } finally {
        this.switchBusy = false;
      }
    },
    async onTurnOff() {
      if (this.switchBusy) return;
      this.clearFailure();
      this.switchBusy = true;
      try {
        const res = await saveFormsSettingsSection({ auth: this.auth(), siteID: this.siteId, section: 'medHx', value: { activated: false } });
        this.activated = !!(res && res.settings && res.settings.activated === true); // only the switch changes: any unsaved edits stay unsaved
        this.$emit('toast', 'Medical history reminders are off');
      } catch (e) {
        this.showFailure(e, 'Couldn’t turn this off. Please try again.');
      } finally {
        this.switchBusy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.mh { max-width: 1320px; margin: 0 auto; padding: 22px 32px 40px; display: flex; flex-direction: column; gap: 16px; }
.mh-top { display: flex; flex-direction: column; gap: 12px; }
.mh-cols { display: flex; flex-wrap: wrap; gap: 24px; align-items: flex-start; }
.mh-left { flex: 1 1 540px; min-width: 0; display: flex; flex-direction: column; gap: 16px; }

.mh-problems {
  margin: 0;
  padding: 10px 14px 10px 30px;
  border: 1px solid $pp-red-border;
  border-radius: 10px;
  background: $pp-red-subtle;
  font-size: 13.5px;
  font-weight: 600;
  color: $pfe-red-ink;
}

.mh-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mh-card-title { font-family: $pp-font-display; font-weight: 800; font-size: 16px; letter-spacing: -0.01em; }
.mh-sub { font-size: 13px; color: $pfe-neutral-500; }
.mh-msg-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; padding-top: 4px; }
.mh-cards { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.mh-text { margin: 0; font-size: 14.5px; line-height: 1.55; }
.mh-help { font-size: 12.5px; line-height: 1.45; color: $pfe-neutral-500; }
.mh-help--lead { font-size: 14px; color: $pp-text; }

.mh-line { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 14.5px; }
.mh-num, .mh-select {
  @include pfe-field(40px);
  height: 40px;
  padding: 0 10px;

  &:focus { @include pfe-focus-ring; }
}
.mh-num { width: 64px; text-align: center; font-variant-numeric: tabular-nums; }
.mh-select { min-width: 110px; }
.mh-select--wide { width: 100%; max-width: 480px; }
.mh-field--bad { border-color: #d9534f !important; }
.mh-error { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 600; color: $pfe-red-ink; }

.mh-note-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  font-size: 13.5px;
  color: $pfe-neutral-600;

  i { color: $pfe-neutral-400; }
}
.mh-link, .mh-link-btn {
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: $pp-brand;
  cursor: pointer;

  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; border-radius: 4px; }
}

.mh-saved { display: flex; flex-direction: column; gap: 8px; }
.mh-saved-label { margin: 0; font-size: 14px; font-weight: 600; }
.mh-saved-note { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 8px; font-size: 13px; color: $pfe-neutral-500; i { color: $pfe-neutral-400; } }
.mh-foot-links { display: flex; justify-content: flex-end; }
.mh-warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid $pp-amber-border;
  border-radius: 10px;
  background: $pp-amber-subtle;
  font-size: 13.5px;
  line-height: 1.45;
  color: $pp-amber-text;

  i { margin-top: 2px; }
}

@media (max-width: 700px) {
  .mh { padding: 18px 16px 32px; }
}
</style>
